"use client";

import { c as _c } from "react/compiler-runtime";
import { useGameLocale, useT } from "@/lib/i18n";
import { hreflangOf } from "@/lib/locale";
/**
 * In-game-style summary of a run, mimicking the victory/defeat screen.
 * Renders three act rows of map node icons + relic strip + card grid.
 */

import { useEffect, useState, type ReactNode } from "react";
import type { TFn } from "@/lib/i18n";
import { Link } from "@/i18n/navigation";
import TinyCard from "@/app/components/TinyCard";
import { CardPill, RelicPill, PotionPill, displayName, type CardInfo, type RelicInfo, type PotionInfo } from "./RunPills";
import { eventChoiceName, restChoiceName, roomTitle, roomTypeTitle, typedRoom } from "./run-names";
import { useTryGameTranslations, type TryGameT } from "@/lib/game-i18n";
import type { EncounterRoom, Floor, Player, PlayerStats, Run } from "@/lib/api/run/types";
export type { PotionInfo };
import { imageUrl } from "@/lib/image-url";
import { stackCards } from "@/lib/deck-stack";
import { fmtDateTime, fmtDateTimePacific } from "@/lib/pacific";
import { PlayerBadge } from "@/app/components/SupporterBadge";
import { OwnerTheme } from "@/app/components/OwnerTheme";
const ICON_BASE = imageUrl("/static/images/ui/run_history");
const RARITY_ORDER = ["Starter", "Common", "Uncommon", "Rare", "Ancient", "Event", "Token", "Status", "Curse", "Quest"];

// Matches NDeckHistoryEntry.Reload() in the game:
//   enchanted → StsColors.purple
//   upgraded  → StsColors.green
//   else      → default label color
function cardLabelColor(upgraded: boolean, enchanted: boolean): string {
  if (enchanted) return "text-[var(--color-necrobinder)]";
  if (upgraded) return "text-[var(--color-silent)]";
  return "text-[var(--text-primary)]";
}
const TIER_LABELS: Record<string, string> = {
  weak: "Weak",
  normal: "Normal",
  elite: "Elite",
  boss: "Boss"
};
const TIER_OUTLINE: Record<string, string> = {
  weak: "ring-1 ring-success/40",
  normal: "ring-1 ring-warning/40",
  elite: "ring-1 ring-warning/60",
  boss: "ring-2 ring-danger/60"
};
const ICON_SLUG: Record<string, string> = {
  monster: "monster",
  elite: "elite",
  event: "event",
  treasure: "treasure",
  rest_site: "rest_site",
  shop: "shop",
  unknown: "event"
};
function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}
const DATE_STYLE = {
  year: "numeric",
  month: "long",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit"
} as const;

// Server-rendered pages must emit the same string everywhere, so the first
// paint is Pacific; after hydration it becomes the viewer's local time.
function modeLabel(mode: string | undefined): string {
  if (!mode) return "Standard";
  return mode.charAt(0).toUpperCase() + mode.slice(1).toLowerCase();
}
function formatDate(epoch: number | undefined, local: boolean, locale: string): string {
  if (!epoch) return "";
  return local ? fmtDateTime(epoch * 1000, DATE_STYLE, locale) : fmtDateTimePacific(epoch * 1000, DATE_STYLE, locale);
}

/** Decide the tier ("weak"|"normal"|"elite"|"boss") for an encounter. */
function encounterTier(room: EncounterRoom): "weak" | "normal" | "elite" | "boss" {
  switch (room.encounter_type) {
    case "BOSS":
      return "boss";
    case "ELITE":
      return "elite";
    default:
      return room.id.endsWith("_WEAK") ? "weak" : "normal";
  }
}

/** The detail page for what the floor held, when it has one. */
function floorHref(floor: Floor, bp: string): string | null {
  const room = typedRoom(floor);
  if (room?.type === "ENCOUNTER") return `${bp}/encounters/${room.id.toLowerCase()}`;
  if (room?.type === "EVENT") return `${bp}/events/${room.id.toLowerCase()}`;
  return null;
}

/** Resolve the icon filename, tier and label for a floor. */
function iconFor(floor: Floor, gt: TryGameT, t: TFn, buildId?: string): {
  src: string;
  betaSrc?: string;
  tier: string;
  label: string;
} {
  const room = typedRoom(floor);
  const tier = room?.type === "ENCOUNTER" ? encounterTier(room) : "";

  // Main run_history path plus a beta-versioned fallback. Beta-only content
  // (e.g. the AEONGLASS boss) only has its map icon under the beta tree, so a
  // beta build's run must fall back to /beta/<build_id>/... when the main path
  // 404s. Known main icons never 404, so the fallback only fires when needed.
  const resolve = (slug: string) => ({
    src: `${ICON_BASE}/${slug}.webp`,
    betaSrc: buildId ? imageUrl(`/static/images/beta/${buildId}/ui/run_history/${slug}.webp`) : undefined
  });
  const label = roomTitle(floor, gt) ?? roomTypeTitle(floor, gt, t);
  if (room?.type === "ENCOUNTER" && room.encounter_type === "BOSS") {
    return {
      ...resolve(room.id.toLowerCase()),
      tier,
      label
    };
  }
  if (floor.floor_type === "ANCIENT" && room?.type === "EVENT") {
    return {
      ...resolve(room.id.toLowerCase()),
      tier: "",
      label
    };
  }
  const slug = ICON_SLUG[floor.raw_type] ?? "monster";
  return {
    ...resolve(slug),
    tier,
    label
  };
}
interface Props {
  run: Run;
  player: Player;
  cardData: Record<string, CardInfo>;
  relicData: Record<string, RelicInfo>;
  potionData: Record<string, PotionInfo>;
  charColor: string;
  langPrefix: string;
  charName: string;
  killedByName?: string;
}
export default function RunSummary(t0) {
  const $ = _c(177);
  const {
    run,
    player,
    cardData,
    relicData,
    potionData,
    charColor,
    langPrefix: bp,
    charName,
    killedByName
  } = t0;
  const t = useT();
  const gt = useTryGameTranslations();
  const dateLocale = hreflangOf(useGameLocale());
  let t1;
  if ($[0] !== cardData || $[1] !== gt) {
    t1 = id => gt(`cards.${id}.title`) ?? cardData[id]?.name ?? displayName(`CARD.${id}`);
    $[0] = cardData;
    $[1] = gt;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const cardTitle = t1;
  const [mounted, setMounted] = useState(false);
  let t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setMounted(true);
    t3 = [];
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== run) {
    t4 = lastPlayerStats(run);
    $[5] = run;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const finalStats = t4;
  let t5;
  if ($[7] !== run.floor_history) {
    t5 = run.floor_history.reduce(_temp, 0);
    $[7] = run.floor_history;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const totalFloors = t5;
  let t6;
  if ($[9] !== player.character) {
    t6 = player.character.toLowerCase();
    $[9] = player.character;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const charSlug = t6;
  const t7 = `/static/images/characters/character_icon_${charSlug}.webp`;
  let t8;
  if ($[11] !== t7) {
    t8 = imageUrl(t7);
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const charIcon = t8;
  const potionSlots = player.max_potion_slot_count ?? 3;
  let t9;
  if ($[13] !== player.potions) {
    t9 = player.potions ?? [];
    $[13] = player.potions;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const playerPotions = t9;
  let t10;
  if ($[15] !== charName || $[16] !== killedByName || $[17] !== run.was_abandoned || $[18] !== run.win || $[19] !== t) {
    t10 = run.win ? t("{char} ascended.", {
      char: charName
    }) : run.was_abandoned ? t("The journey ended.") : killedByName ? t("{char} fell to {encounter}.", {
      char: charName,
      encounter: killedByName
    }) : t("{char} fell.", {
      char: charName
    });
    $[15] = charName;
    $[16] = killedByName;
    $[17] = run.was_abandoned;
    $[18] = run.win;
    $[19] = t;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const deathQuote = t10;
  let t11;
  if ($[21] !== relicData) {
    t11 = r => relicData[r.id]?.rarity;
    $[21] = relicData;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== player.relics || $[24] !== t11) {
    t12 = bucketByRarity(player.relics, t11);
    $[23] = player.relics;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const relicsByRarity = t12;
  let t13;
  if ($[26] !== cardData) {
    t13 = c => cardData[c.id]?.rarity;
    $[26] = cardData;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== player.deck || $[29] !== t13) {
    t14 = bucketByRarity(player.deck, t13);
    $[28] = player.deck;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const cardsByRarity = t14;
  let t15;
  if ($[31] !== gt) {
    t15 = id_0 => gt(`cards.${id_0}.title`);
    $[31] = gt;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  const stackedCards = stackCards(player.deck, cardData, t15);
  const t16 = "rounded-xl border p-4 sm:p-5 mb-4";
  const t17 = `color-mix(in srgb, ${charColor} 35%, transparent)`;
  const t18 = `color-mix(in srgb, ${charColor} 6%, var(--bg-card))`;
  let t19;
  if ($[33] !== t17 || $[34] !== t18) {
    t19 = {
      borderColor: t17,
      background: t18
    };
    $[33] = t17;
    $[34] = t18;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  const t20 = `${bp}/characters/${charSlug}`;
  let t21;
  if ($[36] !== charColor) {
    t21 = {
      borderColor: charColor
    };
    $[36] = charColor;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  if ($[38] !== charIcon || $[39] !== charName || $[40] !== t21) {
    t22 = <img src={charIcon} alt={charName} className="w-9 h-9 rounded-full object-cover border-2" style={t21} crossOrigin="anonymous" />;
    $[38] = charIcon;
    $[39] = charName;
    $[40] = t21;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== t20 || $[43] !== t22) {
    t23 = <Link href={t20} className="flex-shrink-0">{t22}</Link>;
    $[42] = t20;
    $[43] = t22;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  let t24;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = imageUrl("/static/images/ui/top_bar/top_bar_heart.webp");
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  let t25;
  if ($[46] !== t) {
    t25 = t("HP");
    $[46] = t;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  const t26 = `${finalStats?.current_hp ?? "?"}/${finalStats?.max_hp ?? "?"}`;
  let t27;
  if ($[48] !== t25 || $[49] !== t26) {
    t27 = <IconStat icon={t24} alt={t25} value={t26} color="var(--color-ironclad)" />;
    $[48] = t25;
    $[49] = t26;
    $[50] = t27;
  } else {
    t27 = $[50];
  }
  let t28;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = imageUrl("/static/images/ui/top_bar/top_bar_gold.webp");
    $[51] = t28;
  } else {
    t28 = $[51];
  }
  let t29;
  if ($[52] !== t) {
    t29 = t("Gold");
    $[52] = t;
    $[53] = t29;
  } else {
    t29 = $[53];
  }
  const t30 = finalStats?.current_gold ?? "?";
  let t31;
  if ($[54] !== t29 || $[55] !== t30) {
    t31 = <IconStat icon={t28} alt={t29} value={t30} color="var(--accent-gold)" />;
    $[54] = t29;
    $[55] = t30;
    $[56] = t31;
  } else {
    t31 = $[56];
  }
  let t32;
  if ($[57] !== bp || $[58] !== playerPotions || $[59] !== potionData || $[60] !== potionSlots) {
    t32 = <PotionSlots potions={playerPotions} total={potionSlots} potionData={potionData} bp={bp} />;
    $[57] = bp;
    $[58] = playerPotions;
    $[59] = potionData;
    $[60] = potionSlots;
    $[61] = t32;
  } else {
    t32 = $[61];
  }
  let t33;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = imageUrl("/static/images/ui/top_bar/top_bar_map.webp");
    $[62] = t33;
  } else {
    t33 = $[62];
  }
  let t34;
  if ($[63] !== t) {
    t34 = t("Floor");
    $[63] = t;
    $[64] = t34;
  } else {
    t34 = $[64];
  }
  let t35;
  if ($[65] !== t34 || $[66] !== totalFloors) {
    t35 = <IconStat icon={t33} alt={t34} value={totalFloors} />;
    $[65] = t34;
    $[66] = totalFloors;
    $[67] = t35;
  } else {
    t35 = $[67];
  }
  let t36;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = imageUrl("/static/images/ui/top_bar/timer_icon.webp");
    $[68] = t36;
  } else {
    t36 = $[68];
  }
  let t37;
  if ($[69] !== t) {
    t37 = t("Time");
    $[69] = t;
    $[70] = t37;
  } else {
    t37 = $[70];
  }
  const t38 = run.run_time ?? 0;
  let t39;
  if ($[71] !== t38) {
    t39 = formatTime(t38);
    $[71] = t38;
    $[72] = t39;
  } else {
    t39 = $[72];
  }
  let t40;
  if ($[73] !== t37 || $[74] !== t39) {
    t40 = <IconStat icon={t36} alt={t37} value={t39} />;
    $[73] = t37;
    $[74] = t39;
    $[75] = t40;
  } else {
    t40 = $[75];
  }
  let t41;
  if ($[76] !== run.ascension || $[77] !== t) {
    t41 = (run.ascension ?? 0) > 0 && <IconStat icon={imageUrl("/static/images/ui/top_bar/top_bar_ascension.webp")} alt={t("Ascension")} value={`A${run.ascension}`} color="var(--accent-gold)" />;
    $[76] = run.ascension;
    $[77] = t;
    $[78] = t41;
  } else {
    t41 = $[78];
  }
  let t42;
  if ($[79] !== run.username) {
    t42 = <OwnerTheme username={run.username} />;
    $[79] = run.username;
    $[80] = t42;
  } else {
    t42 = $[80];
  }
  let t43;
  if ($[81] !== bp || $[82] !== run.username || $[83] !== t) {
    t43 = run.username && <div className="truncate"><Link href={`${bp}/runs?username=${encodeURIComponent(run.username)}`} className="text-[var(--text-secondary)] hover:text-[var(--accent-gold)] hover:underline" title={t("View all runs by this player")}>{t("by")}{" "}<span className="font-medium text-[var(--text-primary)]">{run.username}</span></Link><PlayerBadge username={run.username} className="ml-1" /></div>;
    $[81] = bp;
    $[82] = run.username;
    $[83] = t;
    $[84] = t43;
  } else {
    t43 = $[84];
  }
  const t44 = run.start_time && <div className="truncate" suppressHydrationWarning={true}>{formatDate(run.start_time, mounted, dateLocale)}</div>;
  let t45;
  if ($[85] !== run.seed || $[86] !== t) {
    t45 = run.seed && <div className="truncate">{t("Seed")}: <span className="font-mono">{run.seed}</span></div>;
    $[85] = run.seed;
    $[86] = t;
    $[87] = t45;
  } else {
    t45 = $[87];
  }
  let t46;
  if ($[88] !== run.game_mode || $[89] !== t) {
    t46 = t(modeLabel(run.game_mode));
    $[88] = run.game_mode;
    $[89] = t;
    $[90] = t46;
  } else {
    t46 = $[90];
  }
  let t47;
  if ($[91] !== run.build_id) {
    t47 = run.build_id && <span className="ml-1">· {run.build_id}</span>;
    $[91] = run.build_id;
    $[92] = t47;
  } else {
    t47 = $[92];
  }
  let t48;
  if ($[93] !== t46 || $[94] !== t47) {
    t48 = <div className="truncate">{t46}{t47}</div>;
    $[93] = t46;
    $[94] = t47;
    $[95] = t48;
  } else {
    t48 = $[95];
  }
  let t49;
  if ($[96] !== t42 || $[97] !== t43 || $[98] !== t44 || $[99] !== t45 || $[100] !== t48) {
    t49 = <div className="w-full sm:w-auto sm:ml-auto text-left sm:text-right text-xs text-[var(--text-muted)] leading-tight">{t42}{t43}{t44}{t45}{t48}</div>;
    $[96] = t42;
    $[97] = t43;
    $[98] = t44;
    $[99] = t45;
    $[100] = t48;
    $[101] = t49;
  } else {
    t49 = $[101];
  }
  let t50;
  if ($[102] !== t23 || $[103] !== t27 || $[104] !== t31 || $[105] !== t32 || $[106] !== t35 || $[107] !== t40 || $[108] !== t41 || $[109] !== t49) {
    t50 = <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm mb-3 pb-3 border-b border-[var(--border-subtle)]">{t23}{t27}{t31}{t32}{t35}{t40}{t41}{t49}</div>;
    $[102] = t23;
    $[103] = t27;
    $[104] = t31;
    $[105] = t32;
    $[106] = t35;
    $[107] = t40;
    $[108] = t41;
    $[109] = t49;
    $[110] = t50;
  } else {
    t50 = $[110];
  }
  let t51;
  if ($[111] !== deathQuote) {
    t51 = <div className="mb-4 italic text-sm text-[var(--text-secondary)]">“{deathQuote}”</div>;
    $[111] = deathQuote;
    $[112] = t51;
  } else {
    t51 = $[112];
  }
  let t52;
  if ($[113] !== bp || $[114] !== cardTitle || $[115] !== gt || $[116] !== potionData || $[117] !== relicData || $[118] !== run.acts || $[119] !== run.build_id || $[120] !== run.floor_history || $[121] !== t) {
    t52 = run.floor_history.map((act_0, i) => {
      const actId = run.acts[i];
      const actName = (actId ? gt(`acts.${actId}.title`) : undefined) ?? t("Act {n}", {
        n: i + 1
      });
      const actStartFloor = run.floor_history.slice(0, i).reduce(_temp2, 0) + 1;
      return <div key={i} className="flex items-center gap-2 sm:gap-3"><div className="w-20 sm:w-24 text-xs font-medium text-[var(--text-secondary)] flex-shrink-0">{actName}</div><div className="flex flex-wrap items-center gap-1 flex-1">{act_0.map((floor, j) => <MapNode key={j} floor={floor} floorNum={actStartFloor + j} bp={bp} buildId={run.build_id} cardTitle={cardTitle} relicData={relicData} potionData={potionData} />)}</div></div>;
    });
    $[113] = bp;
    $[114] = cardTitle;
    $[115] = gt;
    $[116] = potionData;
    $[117] = relicData;
    $[118] = run.acts;
    $[119] = run.build_id;
    $[120] = run.floor_history;
    $[121] = t;
    $[122] = t52;
  } else {
    t52 = $[122];
  }
  let t53;
  if ($[123] !== t52) {
    t53 = <div className="space-y-2 mb-5">{t52}</div>;
    $[123] = t52;
    $[124] = t53;
  } else {
    t53 = $[124];
  }
  let t54;
  if ($[125] !== t) {
    t54 = t("Relics");
    $[125] = t;
    $[126] = t54;
  } else {
    t54 = $[126];
  }
  let t55;
  if ($[127] !== player.relics.length || $[128] !== t54) {
    t55 = <span className="font-semibold">{t54} ({player.relics.length}):</span>;
    $[127] = player.relics.length;
    $[128] = t54;
    $[129] = t55;
  } else {
    t55 = $[129];
  }
  let t56;
  if ($[130] !== relicsByRarity || $[131] !== t) {
    t56 = <RaritySummary buckets={relicsByRarity} t={t} />;
    $[130] = relicsByRarity;
    $[131] = t;
    $[132] = t56;
  } else {
    t56 = $[132];
  }
  let t57;
  if ($[133] !== t55 || $[134] !== t56) {
    t57 = <div className="text-xs text-[var(--text-secondary)] mb-2">{t55}{" "}{t56}</div>;
    $[133] = t55;
    $[134] = t56;
    $[135] = t57;
  } else {
    t57 = $[135];
  }
  let t58;
  if ($[136] !== bp || $[137] !== gt || $[138] !== player.relics || $[139] !== relicData) {
    let t59;
    if ($[141] !== bp || $[142] !== gt || $[143] !== relicData) {
      t59 = (relic, i_0) => {
        const info = relicData[relic.id];
        return <RelicPill key={`${relic.id}-${i_0}`} relicId={relic.id} relicData={relicData} bp={bp} className="w-8 h-8 sm:w-9 sm:h-9 rounded-md bg-scrim/30 flex items-center justify-center hover:bg-scrim/50 transition-colors">{info?.image_url ? <img src={imageUrl(info.image_url)} alt={gt(`relics.${relic.id}.title`) ?? info.name} className="w-full h-full object-contain p-0.5" crossOrigin="anonymous" /> : <span className="text-[8px] text-[var(--text-muted)]">{relic.id.slice(0, 3)}</span>}</RelicPill>;
      };
      $[141] = bp;
      $[142] = gt;
      $[143] = relicData;
      $[144] = t59;
    } else {
      t59 = $[144];
    }
    t58 = player.relics.map(t59);
    $[136] = bp;
    $[137] = gt;
    $[138] = player.relics;
    $[139] = relicData;
    $[140] = t58;
  } else {
    t58 = $[140];
  }
  let t59;
  if ($[145] !== t58) {
    t59 = <div className="flex flex-wrap gap-1">{t58}</div>;
    $[145] = t58;
    $[146] = t59;
  } else {
    t59 = $[146];
  }
  let t60;
  if ($[147] !== t57 || $[148] !== t59) {
    t60 = <div className="mb-4">{t57}{t59}</div>;
    $[147] = t57;
    $[148] = t59;
    $[149] = t60;
  } else {
    t60 = $[149];
  }
  let t61;
  if ($[150] !== t) {
    t61 = t("Cards");
    $[150] = t;
    $[151] = t61;
  } else {
    t61 = $[151];
  }
  let t62;
  if ($[152] !== player.deck.length || $[153] !== t61) {
    t62 = <span className="font-semibold">{t61} ({player.deck.length}):</span>;
    $[152] = player.deck.length;
    $[153] = t61;
    $[154] = t62;
  } else {
    t62 = $[154];
  }
  let t63;
  if ($[155] !== cardsByRarity || $[156] !== t) {
    t63 = <RaritySummary buckets={cardsByRarity} t={t} />;
    $[155] = cardsByRarity;
    $[156] = t;
    $[157] = t63;
  } else {
    t63 = $[157];
  }
  let t64;
  if ($[158] !== t62 || $[159] !== t63) {
    t64 = <div className="text-xs text-[var(--text-secondary)] mb-2">{t62}{" "}{t63}</div>;
    $[158] = t62;
    $[159] = t63;
    $[160] = t64;
  } else {
    t64 = $[160];
  }
  const t65 = "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-1";
  let t66;
  if ($[161] !== bp || $[162] !== cardData || $[163] !== cardTitle) {
    t66 = (entry, i_1) => {
      const info_0 = cardData[entry.id];
      const colorClass = cardLabelColor(entry.upgraded, !!entry.enchantment);
      return <CardPill key={`${entry.id}-${entry.upgraded ? "u" : "n"}-${entry.enchantment ?? ""}-${i_1}`} cardId={entry.id} upgraded={entry.upgraded} enchantment={entry.enchantment} cardData={cardData} bp={bp} className="flex items-center gap-1.5 text-xs hover:bg-[var(--bg-card-hover)] rounded px-1 py-0.5 transition-colors"><TinyCard color={info_0?.color} type={info_0?.type} rarity={info_0?.rarity} /><span className={`truncate ${colorClass}`}>{entry.count > 1 && <span className="text-[var(--text-muted)] mr-1">{entry.count}x</span>}{cardTitle(entry.id)}{entry.upgraded && "+"}</span></CardPill>;
    };
    $[161] = bp;
    $[162] = cardData;
    $[163] = cardTitle;
    $[164] = t66;
  } else {
    t66 = $[164];
  }
  const t67 = stackedCards.map(t66);
  let t68;
  if ($[165] !== t67) {
    t68 = <div className={t65}>{t67}</div>;
    $[165] = t67;
    $[166] = t68;
  } else {
    t68 = $[166];
  }
  let t69;
  if ($[167] !== t64 || $[168] !== t68) {
    t69 = <div>{t64}{t68}</div>;
    $[167] = t64;
    $[168] = t68;
    $[169] = t69;
  } else {
    t69 = $[169];
  }
  let t70;
  if ($[170] !== t19 || $[171] !== t50 || $[172] !== t51 || $[173] !== t53 || $[174] !== t60 || $[175] !== t69) {
    t70 = <div className={t16} style={t19}>{t50}{t51}{t53}{t60}{t69}</div>;
    $[170] = t19;
    $[171] = t50;
    $[172] = t51;
    $[173] = t53;
    $[174] = t60;
    $[175] = t69;
    $[176] = t70;
  } else {
    t70 = $[176];
  }
  return t70;
}
function _temp2(sum_0, a) {
  return sum_0 + a.length;
}
function _temp(sum, act) {
  return sum + act.length;
}
function MapNode(t0) {
  const $ = _c(53);
  const {
    floor,
    floorNum,
    bp,
    buildId,
    cardTitle,
    relicData,
    potionData
  } = t0;
  const t = useT();
  const gt = useTryGameTranslations();
  const [show, setShow] = useState(false);
  let t1;
  if ($[0] !== buildId || $[1] !== floor || $[2] !== gt || $[3] !== t) {
    t1 = iconFor(floor, gt, t, buildId);
    $[0] = buildId;
    $[1] = floor;
    $[2] = gt;
    $[3] = t;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const {
    src,
    betaSrc,
    tier,
    label
  } = t1;
  let href;
  let iconImg;
  let t2;
  if ($[5] !== betaSrc || $[6] !== bp || $[7] !== cardTitle || $[8] !== floor || $[9] !== floorNum || $[10] !== gt || $[11] !== label || $[12] !== potionData || $[13] !== relicData || $[14] !== show || $[15] !== src || $[16] !== t || $[17] !== tier) {
    const room = typedRoom(floor);
    const ps = floor.player_stats[0];
    let t3;
    if ($[21] !== gt || $[22] !== relicData) {
      t3 = id => gt(`relics.${id}.title`) ?? relicData[id]?.name ?? displayName(`RELIC.${id}`);
      $[21] = gt;
      $[22] = relicData;
      $[23] = t3;
    } else {
      t3 = $[23];
    }
    const relicTitle = t3;
    let t4;
    if ($[24] !== gt || $[25] !== potionData) {
      t4 = id_0 => gt(`potions.${id_0}.title`) ?? potionData[id_0]?.name ?? displayName(`POTION.${id_0}`);
      $[24] = gt;
      $[25] = potionData;
      $[26] = t4;
    } else {
      t4 = $[26];
    }
    const potionTitle = t4;
    let t5;
    if ($[27] !== cardTitle || $[28] !== potionTitle || $[29] !== relicTitle) {
      t5 = (table, id_1) => {
        if (table === "cards") {
          return cardTitle(id_1);
        }
        if (table === "potions") {
          return potionTitle(id_1);
        }
        return relicTitle(id_1);
      };
      $[27] = cardTitle;
      $[28] = potionTitle;
      $[29] = relicTitle;
      $[30] = t5;
    } else {
      t5 = $[30];
    }
    const pickTitle = t5;
    let t6;
    if ($[31] !== bp || $[32] !== floor) {
      t6 = floorHref(floor, bp);
      $[31] = bp;
      $[32] = floor;
      $[33] = t6;
    } else {
      t6 = $[33];
    }
    href = t6;
    const turns = room?.type === "ENCOUNTER" && room.turns_taken != null ? gt("run_history.MAP_POINT_HISTORY.turnsTaken", {
      Turns: room.turns_taken
    }) ?? `${room.turns_taken} ${t("turns")}` : undefined;
    const chose = ps?.event_choices?.map(c => eventChoiceName(c, gt)).filter(_temp3) ?? [];
    const rested = ps?.rest_site_choices?.map(c_0 => restChoiceName(c_0, gt, t));
    const ancientPicks = ps?.ancient_choices?.filter(_temp4).map(c_2 => pickTitle(c_2.table, c_2.id)) ?? [];
    let t7;
    if ($[34] !== betaSrc) {
      t7 = e => {
        const img = e.currentTarget as HTMLImageElement;
        if (betaSrc && img.dataset.triedBeta !== "1") {
          img.dataset.triedBeta = "1";
          img.src = betaSrc;
          return;
        }
        img.style.display = "none";
      };
      $[34] = betaSrc;
      $[35] = t7;
    } else {
      t7 = $[35];
    }
    let t8;
    if ($[36] !== label || $[37] !== src || $[38] !== t7) {
      t8 = <img src={src} alt={label} className="w-full h-full object-contain p-0.5" crossOrigin="anonymous" onError={t7} />;
      $[36] = label;
      $[37] = src;
      $[38] = t7;
      $[39] = t8;
    } else {
      t8 = $[39];
    }
    iconImg = t8;
    t2 = show && <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-card)] shadow-xl pointer-events-none text-left"><div className="flex items-center justify-between mb-1.5"><div className="text-xs font-semibold text-[var(--text-primary)]">{label}</div><div className="text-[10px] text-[var(--text-muted)]">{gt("run_history.MAP_POINT_HISTORY.header", {
            FloorNum: floorNum
          }) ?? t("Floor {n}", {
            n: floorNum
          })}</div></div><div className="text-[10px] text-[var(--text-muted)] mb-1.5 capitalize">{roomTypeTitle(floor, gt, t)}{tier && ` · ${t(TIER_LABELS[tier])}`}{turns && ` · ${turns}`}</div>{room?.type === "ENCOUNTER" && room.monsters.length > 0 && <div className="text-[10px] text-[var(--text-secondary)] mb-1.5">{t("vs")}{" "}{room.monsters.map(m => gt(`monsters.${m}.name`) ?? displayName(`MONSTER.${m}`)).join(", ")}</div>}{ps && <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-[10px] text-[var(--text-muted)] mb-1.5"><span>{t("HP")} {ps.current_hp}/{ps.max_hp}</span>{(ps.damage_taken ?? 0) > 0 && <span style={{
          color: "var(--color-ironclad)"
        }}>-{ps.damage_taken}</span>}{(ps.hp_healed ?? 0) > 0 && <span style={{
          color: "var(--color-silent)"
        }}>+{ps.hp_healed} {t("HP")}</span>}{(ps.gold_gained ?? 0) > 0 && <span style={{
          color: "var(--accent-gold)"
        }}>+{ps.gold_gained}g</span>}{(ps.gold_spent ?? 0) > 0 && <span style={{
          color: "var(--text-muted)"
        }}>-{ps.gold_spent}g</span>}</div>}{rested && rested.length > 0 && <div className="text-[10px] text-[var(--text-secondary)] mb-0.5">{rested.join(", ")}</div>}{ps?.cards_gained && ps.cards_gained.length > 0 && <div className="text-[10px] text-[var(--color-silent)] mb-0.5">+ {ps.cards_gained.map(c_3 => cardTitle(c_3.id)).join(", ")}</div>}{ps?.cards_removed && ps.cards_removed.length > 0 && <div className="text-[10px] text-[var(--color-ironclad)] mb-0.5">− {ps.cards_removed.map(c_4 => cardTitle(c_4.id)).join(", ")}</div>}{ps?.cards_transformed && ps.cards_transformed.length > 0 && <div className="text-[10px] text-[var(--color-necrobinder)] mb-0.5">{ps.cards_transformed.map(c_5 => `${cardTitle(c_5.original_card.id)} → ${cardTitle(c_5.final_card.id)}`).join(", ")}</div>}{ps?.upgraded_cards && ps.upgraded_cards.length > 0 && <div className="text-[10px] text-[var(--accent-gold)] mb-0.5">⬆ {ps.upgraded_cards.map(c_6 => cardTitle(c_6)).join(", ")}</div>}{ps?.relic_choices?.some(_temp5) && <div className="text-[10px] text-[var(--accent-gold)] mb-0.5">+{" "}{ps.relic_choices.filter(_temp6).map(r_0 => relicTitle(r_0.choice)).join(", ")}</div>}{ps?.potion_choices?.some(_temp7) && <div className="text-[10px] text-[var(--accent-teal)] mb-0.5">+{" "}{ps.potion_choices.filter(_temp8).map(p_0 => potionTitle(p_0.choice)).join(", ")}</div>}{ancientPicks.length > 0 && <div className="text-[10px] text-[var(--accent-gold)] mb-0.5">+ {ancientPicks.join(", ")}</div>}{chose.length > 0 && <div className="text-[10px] text-[var(--text-secondary)] mt-1 italic">{gt("run_history.MAP_POINT_HISTORY.chose", {
          Choice: chose.join(", ")
        }) ?? t("chose {choice}", {
          choice: chose.join(", ")
        })}</div>}<div className="absolute left-1/2 -translate-x-1/2 top-full w-2 h-2 bg-[var(--bg-card)] border-r border-b border-[var(--border-subtle)] rotate-45 -mt-1" /></div>;
    $[5] = betaSrc;
    $[6] = bp;
    $[7] = cardTitle;
    $[8] = floor;
    $[9] = floorNum;
    $[10] = gt;
    $[11] = label;
    $[12] = potionData;
    $[13] = relicData;
    $[14] = show;
    $[15] = src;
    $[16] = t;
    $[17] = tier;
    $[18] = href;
    $[19] = iconImg;
    $[20] = t2;
  } else {
    href = $[18];
    iconImg = $[19];
    t2 = $[20];
  }
  const tooltip = t2;
  const wrapClass = `relative w-7 h-7 sm:w-8 sm:h-8 rounded-md bg-scrim/30 flex items-center justify-center ${TIER_OUTLINE[tier] ?? ""}`;
  if (href) {
    const t3 = `${wrapClass} hover:bg-scrim/50 hover:ring-2 hover:ring-[var(--accent-gold)]/60 transition-all`;
    let t4;
    let t5;
    if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = () => setShow(true);
      t5 = () => setShow(false);
      $[40] = t4;
      $[41] = t5;
    } else {
      t4 = $[40];
      t5 = $[41];
    }
    let t6;
    if ($[42] !== href || $[43] !== iconImg || $[44] !== t3 || $[45] !== tooltip) {
      t6 = <Link href={href} className={t3} onMouseEnter={t4} onMouseLeave={t5}>{iconImg}{tooltip}</Link>;
      $[42] = href;
      $[43] = iconImg;
      $[44] = t3;
      $[45] = tooltip;
      $[46] = t6;
    } else {
      t6 = $[46];
    }
    return t6;
  }
  let t3;
  let t4;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setShow(true);
    t4 = () => setShow(false);
    $[47] = t3;
    $[48] = t4;
  } else {
    t3 = $[47];
    t4 = $[48];
  }
  let t5;
  if ($[49] !== iconImg || $[50] !== tooltip || $[51] !== wrapClass) {
    t5 = <span className={wrapClass} onMouseEnter={t3} onMouseLeave={t4}>{iconImg}{tooltip}</span>;
    $[49] = iconImg;
    $[50] = tooltip;
    $[51] = wrapClass;
    $[52] = t5;
  } else {
    t5 = $[52];
  }
  return t5;
}
function _temp8(p) {
  return p.was_picked;
}
function _temp7(p_1) {
  return p_1.was_picked;
}
function _temp6(r) {
  return r.was_picked;
}
function _temp5(r_1) {
  return r_1.was_picked;
}
function _temp4(c_1) {
  return c_1.was_picked;
}
function _temp3(name) {
  return !!name;
}
function IconStat(t0) {
  const $ = _c(11);
  const {
    icon,
    alt,
    value,
    color
  } = t0;
  let t1;
  if ($[0] !== alt || $[1] !== icon) {
    t1 = <img src={icon} alt={alt} className="w-5 h-5 object-contain" crossOrigin="anonymous" />;
    $[0] = alt;
    $[1] = icon;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== color) {
    t2 = color ? {
      color
    } : undefined;
    $[3] = color;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2 || $[6] !== value) {
    t3 = <span className="font-semibold" style={t2}>{value}</span>;
    $[5] = t2;
    $[6] = value;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t3) {
    t4 = <div className="flex items-center gap-1.5 text-xs sm:text-sm">{t1}{t3}</div>;
    $[8] = t1;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}
function PotionSlots(t0) {
  const $ = _c(14);
  const {
    potions,
    total,
    potionData,
    bp
  } = t0;
  const gt = useTryGameTranslations();
  let t1;
  let t2;
  if ($[0] !== bp || $[1] !== gt || $[2] !== potionData || $[3] !== potions || $[4] !== total) {
    const bySlot = Array(total).fill(null);
    for (const p of potions) {
      if (p.slot_index >= 0 && p.slot_index < total) {
        bySlot[p.slot_index] = p;
      }
    }
    t1 = "flex items-center gap-1";
    let t3;
    if ($[7] !== bp || $[8] !== gt || $[9] !== potionData) {
      t3 = (p_0, i) => {
        if (!p_0) {
          return <span key={i} className="w-5 h-5 rounded-sm border border-dashed border-[var(--border-subtle)]" />;
        }
        const info = potionData[p_0.id];
        return <PotionPill key={i} potionId={p_0.id} potionData={potionData} bp={bp} className="w-5 h-5 flex items-center justify-center hover:scale-110 transition-transform">{info?.image_url ? <img src={imageUrl(info.image_url)} alt={gt(`potions.${p_0.id}.title`) ?? info.name} className="w-5 h-5 object-contain" crossOrigin="anonymous" /> : <span className="w-5 h-5 rounded-sm bg-[var(--color-silent)]/50" />}</PotionPill>;
      };
      $[7] = bp;
      $[8] = gt;
      $[9] = potionData;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    t2 = bySlot.map(t3);
    $[0] = bp;
    $[1] = gt;
    $[2] = potionData;
    $[3] = potions;
    $[4] = total;
    $[5] = t1;
    $[6] = t2;
  } else {
    t1 = $[5];
    t2 = $[6];
  }
  let t3;
  if ($[11] !== t1 || $[12] !== t2) {
    t3 = <div className={t1}>{t2}</div>;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
}
function RaritySummary(t0) {
  const $ = _c(5);
  const {
    buckets,
    t
  } = t0;
  let parts;
  if ($[0] !== buckets || $[1] !== t) {
    parts = [];
    for (const r of RARITY_ORDER) {
      const n = buckets.get(r);
      if (n) {
        parts.push(`${n} ${t(r)}`);
      }
    }
    $[0] = buckets;
    $[1] = t;
    $[2] = parts;
  } else {
    parts = $[2];
  }
  const t1 = parts.join(", ");
  let t2;
  if ($[3] !== t1) {
    t2 = <span className="text-[var(--text-muted)]">{t1}</span>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function bucketByRarity<T>(items: T[], getRarity: (item: T) => string | undefined): Map<string, number> {
  const m = new Map<string, number>();
  for (const item of items) {
    const r = getRarity(item) ?? "Unknown";
    m.set(r, (m.get(r) ?? 0) + 1);
  }
  return m;
}
function lastPlayerStats(run: Run): PlayerStats | undefined {
  const acts = run.floor_history;
  for (let a = acts.length - 1; a >= 0; a--) {
    for (let f = acts[a].length - 1; f >= 0; f--) {
      const ps = acts[a][f]?.player_stats[0];
      if (ps) return ps;
    }
  }
  return undefined;
}